import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { User } from 'firebase/auth';
import ProjectStore from '../flux/stores/ProjectStore';
import AuthStore, { type WorkspaceStatus } from '../flux/stores/AuthStore';
import UIStore from '../flux/stores/UIStore';
import Actions from '../flux/Actions';
import ContentArea from './ContentArea';
import Modal from './Modal';
import TitleBar from './TitleBar';
import { useToast } from './Toast';
import ProjectSidebar from './dashboard/ProjectSidebar';
import ProjectsDashboard from './dashboard/ProjectsDashboard';
import ExportProjectDialog from './dashboard/ExportProjectDialog';
import UtilityTopBar from './dashboard/UtilityTopBar';
import { type Project } from '../types/entities';

function NativeSidebarIcon() {
   return (
      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
         <rect x="3.5" y="5" width="17" height="14" rx="2.5" strokeWidth={1.7} />
         <path strokeLinecap="round" strokeWidth={1.7} d="M9 5v14" />
      </svg>
   );
}

function Dashboard() {
   const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
   const [projects, setProjects] = useState<Project[]>([]);
   const [activeTab, setActiveTab] = useState('scenes');
   const [modalType, setModalType] = useState(null);
   const previousProjectId = useRef(null);
   const projectsLoadedRef = useRef(false);
   const [menuOpen, setMenuOpen] = useState(false);
   const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
   const [user, setUser] = useState<User | null>(() => AuthStore.getUser());
   const isMacElectron = typeof window !== 'undefined' && window.electronAPI?.platform === 'darwin';
   const [isFullScreen, setIsFullScreen] = useState(false);
   const [isOnline, setIsOnline] = useState(
      typeof navigator === 'undefined' ? true : navigator.onLine,
   );
   const { showToast } = useToast();
   const [exportStatus, setExportStatus] = useState<'idle' | 'exporting' | 'success' | 'error'>(
      'idle',
   );
   const [exportDialogOpen, setExportDialogOpen] = useState(false);
   const [isImporting, setIsImporting] = useState(false);
   const [workspaceStatus, setWorkspaceStatus] = useState<WorkspaceStatus>('SIGNED_OUT');
   const [isSyncBlocked, setIsSyncBlocked] = useState(false);
   const [syncBlockedMessage, setSyncBlockedMessage] = useState<string | null>(null);
   const [isSyncing, setIsSyncing] = useState(false);
   const [projectsLoading, setProjectsLoading] = useState(true);
   const menuRef = useRef<HTMLDivElement | null>(null);

   useEffect(() => {
      const handleProjectChange = () => {
         const projectId = ProjectStore.getSelectedProjectId();
         setSelectedProjectId(projectId);
         setProjects(ProjectStore.getProjects());
         if (ProjectStore.isProjectsInitialized()) {
            setProjectsLoading(false);
         }

         const storeError = ProjectStore.getError();
         if (storeError) {
            showToast(storeError, 'error');
            Actions.clearError();
         }

         if (!projectId) {
            previousProjectId.current = null;
            return;
         }

         if (projectId !== previousProjectId.current) {
            previousProjectId.current = projectId;
            Actions.loadScenes(projectId);
            Actions.loadCharacters(projectId);
            Actions.loadLocations(projectId);
            Actions.loadPlots(projectId);
         }
      };

      const handleUIChange = () => {
         setActiveTab(UIStore.getActiveTab());
         setModalType(UIStore.getModalType());
      };

      const handleAuthChange = () => {
         const user = AuthStore.getUser();
         const status = AuthStore.getWorkspaceStatus();
         setUser(user);
         setWorkspaceStatus(status);
         setIsSyncBlocked(AuthStore.isSyncBlocked());
         setSyncBlockedMessage(AuthStore.getSyncBlockedMessage());
         setIsSyncing(AuthStore.isSyncActive());
         if (user) {
            if (status === 'READY' && !projectsLoadedRef.current) {
               projectsLoadedRef.current = true;
               Actions.loadProjects(user.uid);
            }
         }
      };

      const handleClickAway = (e: MouseEvent) => {
         if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
            setMenuOpen(false);
         }
      };

      ProjectStore.addChangeListener(handleProjectChange);
      UIStore.addChangeListener(handleUIChange);
      AuthStore.addChangeListener(handleAuthChange);
      document.addEventListener('mousedown', handleClickAway);

      const handleOnline = () => {
         setIsOnline(true);
         showToast('You are back online', 'success');
      };
      const handleOffline = () => {
         setIsOnline(false);
         showToast('You are offline', 'warning');
      };
      window.addEventListener('online', handleOnline);
      window.addEventListener('offline', handleOffline);

      handleAuthChange();
      handleProjectChange();
      handleUIChange();

      return () => {
         ProjectStore.removeChangeListener(handleProjectChange);
         UIStore.removeChangeListener(handleUIChange);
         AuthStore.removeChangeListener(handleAuthChange);
         document.removeEventListener('mousedown', handleClickAway);
         window.removeEventListener('online', handleOnline);
         window.removeEventListener('offline', handleOffline);
      };
   }, [showToast]);

   const handleOpenExportDialog = useCallback(() => {
      setMenuOpen(false);
      if (!ProjectStore.getSelectedProject()) {
         showToast('Select a project before exporting.', 'warning');
         return;
      }
      setExportDialogOpen(true);
   }, [showToast]);

   const handleExport = async () => {
      const project = ProjectStore.getSelectedProject();
      if (!project) return;

      setExportStatus('exporting');

      try {
         if (!window.electronAPI) {
            throw new Error('Project files can only be exported from the desktop app.');
         }
         const exportData = await Actions.buildProjectFile(project.id!);
         const result = await window.electronAPI.exportProjectFile(
            project.title || 'project',
            exportData,
         );
         if (result.canceled) {
            setExportStatus('idle');
            return;
         }
         if ('error' in result) {
            throw new Error(result.error);
         }

         setExportDialogOpen(false);
         setExportStatus('success');
         showToast('Project exported successfully.', 'success');
         setTimeout(() => setExportStatus('idle'), 3000);
      } catch (err) {
         console.error('Export failed:', err);
         setExportStatus('error');
         showToast(
            err instanceof Error ? err.message : 'The project could not be exported.',
            'error',
         );
         setTimeout(() => setExportStatus('idle'), 3000);
      }
   };

   const handleImport = useCallback(async () => {
      setMenuOpen(false);
      if (isImporting) return;
      if (!window.electronAPI) {
         showToast('Project files can only be imported from the desktop app.', 'error');
         return;
      }

      setIsImporting(true);
      try {
         const result = await window.electronAPI.importProjectFile();
         if (result.canceled) return;
         if ('error' in result) {
            throw new Error(result.error);
         }
         if (!('data' in result)) {
            throw new Error('The selected project file could not be read.');
         }
         await Actions.importProjectFile(result.data);
         showToast('Project imported successfully.', 'success');
      } catch (error) {
         console.error('Import failed:', error);
         showToast(
            error instanceof Error ? error.message : 'The project could not be imported.',
            'error',
         );
      } finally {
         setIsImporting(false);
      }
   }, [isImporting, showToast]);

   useEffect(() => {
      if (!window.electronAPI) return;
      const removeExportListener =
         window.electronAPI.onExportProjectRequested(handleOpenExportDialog);
      const removeImportListener = window.electronAPI.onImportProjectRequested(() => {
         void handleImport();
      });
      return () => {
         removeExportListener();
         removeImportListener();
      };
   }, [handleImport, handleOpenExportDialog]);

   useEffect(() => {
      if (!isMacElectron || !window.electronAPI) return;

      let isMounted = true;
      const removeListener = window.electronAPI.onFullScreenChange(setIsFullScreen);

      window.electronAPI.getFullScreenState().then((fullScreenState) => {
         if (isMounted) {
            setIsFullScreen(fullScreenState);
         }
      });

      return () => {
         isMounted = false;
         removeListener();
      };
   }, [isMacElectron]);

   const handleNewProject = () => {
      Actions.showModal('newProject');
   };

   const handleSelectProject = (projectId: string) => {
      setMenuOpen(false);
      Actions.selectProject(projectId);
   };

   const handleEditProject = (projectId: string) => {
      Actions.showModal('editProject', { projectId });
   };

   const handleDeleteProject = (projectId: string) => {
      if (window.confirm('Are you sure you want to delete this project?')) {
         Actions.deleteProject(projectId);
      }
   };

   const handleBackToProjects = () => {
      setMenuOpen(false);
      Actions.selectProject(null);
   };

   const selectedProject = ProjectStore.getSelectedProject();
   const showTitlebarInset = isMacElectron && !isFullScreen;
   const sidebarToggleTopClass = showTitlebarInset ? 'top-[10px]' : 'top-2';
   const sidebarToggleLeftClass = isSidebarCollapsed
      ? showTitlebarInset
         ? 'left-[86px]'
         : 'left-3'
      : 'left-[280px]';
   const handleToggleSidebar = () => {
      setMenuOpen(false);
      setIsSidebarCollapsed((collapsed) => !collapsed);
   };

   return (
      <div className="h-screen flex flex-col bg-ink">
         <TitleBar />

         <div className="relative flex flex-1 overflow-hidden">
            {selectedProjectId ? (
               <>
                  <button
                     type="button"
                     onClick={handleToggleSidebar}
                     className={`no-drag absolute z-50 flex h-7 w-7 items-center justify-center rounded-md text-silver/90 transition-[left,background-color,color] duration-200 ease-out hover:bg-white/10 hover:text-pearl ${sidebarToggleTopClass} ${sidebarToggleLeftClass}`}
                     title={isSidebarCollapsed ? 'Show Sidebar' : 'Hide Sidebar'}
                     aria-label={isSidebarCollapsed ? 'Show Sidebar' : 'Hide Sidebar'}
                  >
                     <NativeSidebarIcon />
                  </button>
                  <ProjectSidebar
                     activeTab={activeTab}
                     onBackToProjects={handleBackToProjects}
                     showTitlebarInset={showTitlebarInset}
                     isCollapsed={isSidebarCollapsed}
                  />
                  <div className="min-w-0 flex-1 flex flex-col overflow-hidden">
                     <UtilityTopBar
                        showTitlebarInset={showTitlebarInset}
                        projectTitle={selectedProject?.title || 'Untitled Project'}
                        menuRef={menuRef}
                        menuOpen={menuOpen}
                        onMenuToggle={() => setMenuOpen((o) => !o)}
                        onMenuClose={() => setMenuOpen(false)}
                        selectedProjectId={selectedProjectId}
                        onExport={handleOpenExportDialog}
                        onImport={() => void handleImport()}
                        isImporting={isImporting}
                     />
                     <ContentArea activeTab={activeTab} />
                  </div>
               </>
            ) : (
               <ProjectsDashboard
                  projects={projects}
                  userId={AuthStore.getUser()?.uid || null}
                  onSelectProject={handleSelectProject}
                  onNewProject={handleNewProject}
                  onEditProject={handleEditProject}
                  onDeleteProject={handleDeleteProject}
                  isLoading={projectsLoading}
                  workspaceStatus={workspaceStatus}
                  isOnline={isOnline}
                  exportStatus={exportStatus}
                  isSyncBlocked={isSyncBlocked}
                  syncBlockedMessage={syncBlockedMessage}
                  isSyncing={isSyncing}
                  menuRef={menuRef}
                  menuOpen={menuOpen}
                  onMenuToggle={() => setMenuOpen((o) => !o)}
                  onMenuClose={() => setMenuOpen(false)}
                  onImport={() => void handleImport()}
                  isImporting={isImporting}
                  showTitlebarInset={showTitlebarInset}
               />
            )}
         </div>

         {modalType && <Modal />}
         <ExportProjectDialog
            isOpen={exportDialogOpen}
            project={selectedProject}
            counts={{
               scenes: ProjectStore.getScenes().length,
               characters: ProjectStore.getCharacters().length,
               locations: ProjectStore.getLocations().length,
               plots: ProjectStore.getPlots().length,
            }}
            isExporting={exportStatus === 'exporting'}
            onClose={() => setExportDialogOpen(false)}
            onConfirm={handleExport}
         />
      </div>
   );
}

export default Dashboard;
